<template>
  <div class="row">
    <div class="col">
      <div class="subscription-plan">
        <h1
          class="text-h4 font-weight-thin subscription-plan_header text-center mb-6"
        >
          Subscription Plan
        </h1>

        <div class="pricing-cards">
          <div
            v-for="(product, index) in filteredProducts"
            :key="index + '-product'"
            class="pricing-card"
            :class="{
              'pricing-card--selected': selectedPrice === product.prices[0]?.id
            }"
            @click="selectedPrice = product.prices[0]?.id"
          >
            <div
              class="pricing-card__selected-badge"
              v-if="selectedPrice === product.prices[0]?.id"
            >
              <v-icon color="white" size="32">mdi-check-circle</v-icon>
            </div>
            <div
              class="pricing-card__header default"
              :class="filteredProductsColour(product)"
            >
              <div class="pricing-card__header-content">
                <v-icon color="white" class="pricing-card__icon">
                  {{
                    product.name.toLowerCase().includes('monthly')
                      ? 'mdi-calendar'
                      : 'mdi-calendar-month'
                  }}
                </v-icon>
                <h3 class="pricing-card__title">{{ product.name }}</h3>
              </div>
            </div>

            <div class="pricing-card__content">
              <div class="pricing-card__price" v-if="product.prices.length > 0">
                <template v-if="product.name.toLowerCase().includes('annual')">
                  <div class="pricing-card__save-badge">
                    SAVE {{ discount * 100 }}%
                  </div>
                  <span class="pricing-card__original">
                    £{{ calculateMonthlyPrice() }}
                  </span>
                  <span class="pricing-card__currency">£</span>
                  <span class="pricing-card__amount">
                    {{ product.prices[0].unit_amount / 100 }}
                  </span>
                  <span class="pricing-card__period"
                    >/{{ product.prices[0].interval }}</span
                  >
                </template>
                <template v-else>
                  <span class="pricing-card__currency">£</span>
                  <span class="pricing-card__amount">{{
                    setMonthlyPrice(product.prices[0].unit_amount / 100)
                  }}</span>
                  <span class="pricing-card__period"
                    >/{{ product.prices[0].interval }}</span
                  >
                </template>
              </div>
            </div>

            <div
              class="pricing-card__footer"
              :class="{
                'pricing-card__footer--selected':
                  selectedPrice === product.prices[0]?.id,
                [filteredProductsColour(product)]: true
              }"
              @click.stop="selectedPrice = product.prices[0]?.id"
            >
              {{
                selectedPrice === product.prices[0]?.id
                  ? 'Selected'
                  : 'Select this plan'
              }}
            </div>
          </div>
        </div>

        <div class="mt-8 text-center">
          <v-btn
            :color="$vuetify.theme.themes.light.customPrimaryButtonColor"
            :disabled="!selectedPrice || isLoading"
            @click="createSub()"
            elevation="2"
            x-large
            rounded
          >
            <template v-if="isLoading">
              <v-progress-circular indeterminate size="20" class="mr-2" />
              Processing...
            </template>
            <template v-else>
              Buy Subscription
            </template>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase'
import db from '../../firebase/init'
import { loadStripe } from '@stripe/stripe-js'

export default {
  props: {
    productType: {
      type: Array
    }
  },
  data() {
    return {
      products: [],
      selectedPrice: null,
      isLoading: false,
      discount: process.env.VUE_APP_USER_DISCOUNT,
      discountIncrease: 1.1, // 10% increase
      monthlyPrice: null, // New state to store the monthly price
      stripe: null
    }
  },
  mounted() {
    this.fetchProducts()
    this.loadStripe()
  },
  computed: {
    filteredProductsColour() {
      return () => {
        return 'default'
        // if (product.name.toLowerCase().includes('pro')) {
        //   return 'pro'
        // } else if (product.name.toLowerCase().includes('business')) {
        //   return 'business'
        // } else if (product.name.toLowerCase().includes('enterprise')) {
        //   return 'enterprise'
        // } else {
        //   return 'default'
        // }
      }
    },
    filteredProducts() {
      return this.products
        .filter(product => {
          return this.productType.some(word =>
            product.name.toLowerCase().includes(word.toLowerCase())
          )
        })
        .sort(a => {
          // Use price.interval to determine order
          return a.prices[0].interval === 'month' ? -1 : 1
        })
    }
  },
  methods: {
    async fetchProducts() {
      const productsRef = db.collection('products')
      const querySnapshot = await productsRef.where('active', '==', true).get()

      querySnapshot.forEach(async doc => {
        const pricesRef = await doc.ref.collection('prices')
        const pricesQuerySnapshot = await pricesRef
          .where('active', '==', true)
          .get()

        this.products.push({
          id: doc.id,
          ...doc.data(),
          prices: pricesQuerySnapshot.docs.map(price => ({
            id: price.id,
            ...price.data()
          }))
        })

        // Store the monthly price in the state
        const monthlyPrice = pricesQuerySnapshot.docs.find(
          price =>
            price.data().interval === 'month' && price.data().active === true
        )
        if (monthlyPrice) {
          this.monthlyPrice = monthlyPrice.data().unit_amount / 100
        }
      })
    },

    async createSub() {
      // Check if user is authenticated
      if (!firebase.auth().currentUser) {
        // Save the current page URL to redirect back after login
        await this.$store.dispatch('setRefererUrl', this.$route.fullPath)
        // Redirect to login page
        this.$router.push({ name: 'Login' })
        return
      }

      this.isLoading = true

      // Get the selected product and price details
      const selectedProduct = this.filteredProducts.find(
        product => product.prices[0]?.id === this.selectedPrice
      )
      const price = selectedProduct?.prices[0]?.unit_amount / 100
      const interval = selectedProduct?.prices[0]?.interval
      const productName = selectedProduct?.name.toLowerCase()

      // Track InitiateCheckout event for Meta Pixel
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'InitiateCheckout', {
          value: price,
          currency: 'GBP',
          content_name: productName,
          content_ids: [selectedProduct?.id],
          content_type: 'subscription',
          num_items: 1
        })
      }

      // Check if gtag exists before calling
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-11470825588/gVmLCPT-sbUaEPTQ290q',
          value: price,
          currency: 'GBP',
          transaction_id: this.selectedPrice,
          items: [
            {
              id: selectedProduct?.id,
              name: productName,
              price: price,
              quantity: 1
            }
          ],
          subscription_type: productName,
          billing_interval: interval
        })
      }

      const db = firebase.firestore()
      const collectionRef = db.collection(
        'customers/' + firebase.auth().currentUser.uid + '/checkout_sessions'
      )

      try {
        const docRef = await collectionRef.add({
          price: this.selectedPrice,
          success_url:
            window.location.origin +
            '/payment/success?session_id={CHECKOUT_SESSION_ID}',
          cancel_url: window.location.origin
        })

        docRef.onSnapshot(snap => {
          const { error, url } = snap.data()

          if (error) {
            console.error(`An error occurred: ${error.message}`)
            this.isLoading = false
          }

          if (url) {
            window.open(url, '_self')
            this.isLoading = false
          }
        })
      } catch (error) {
        console.error(`An error occurred: ${error.message}`)
        this.isLoading = false
      }
    },
    calculateMonthlyPrice() {
      return 12 * Number(this.monthlyPrice)
    },
    setMonthlyPrice(price) {
      this.monthlyPrice = price
      return price
    },
    async loadStripe() {
      const stripePublicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY
      this.stripe = await loadStripe(stripePublicKey)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/sass/variables.scss';

.subscription-plan {
  padding: 40px 20px;
  border-radius: 20px;
  background: linear-gradient(145deg, #f6f8f9, #ffffff);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);

  &_header {
    font-size: 2.5rem;
    font-weight: 300;
    color: #2c3e50;
  }
}

.pricing-cards {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin: 0 auto;
  max-width: 900px;
}

.pricing-card {
  flex: 1;
  min-width: 280px;
  max-width: 400px;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  background: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  &--selected {
    border: 2px solid var(--v-primary-base);
    transform: translateY(-5px);
    background: linear-gradient(
        rgba(var(--v-primary-base), 0.05),
        rgba(var(--v-primary-base), 0.05)
      ),
      white;
  }

  &__header {
    padding: 20px;
    text-align: center;
    color: white;

    &-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }

    &.default {
      background: $product_default;
    }

    &.pro {
      background: $product_pro;
    }
    &.business {
      background: $product_business;
    }
    &.enterprise {
      background: $product_enterprise;
    }
  }

  &__icon {
    font-size: 2rem !important;
    margin: 0 !important;
  }

  &__title {
    font-size: 1.5rem;
    margin: 0;
    font-weight: 500;
  }

  &__content {
    position: relative;
    padding: 30px 20px 20px;
    text-align: center;
    flex-grow: 1;
  }

  &__price {
    font-size: 1.2rem;
    color: #2c3e50;
  }

  &__currency {
    font-size: 1.5rem;
    font-weight: 500;
    vertical-align: top;
  }

  &__amount {
    font-size: 3rem;
    font-weight: 700;
  }

  &__period {
    color: #666;
    font-size: 1rem;
  }

  &__selected-badge {
    position: absolute;
    top: 5px;
    right: 5px;
    background: var(--v-primary-base);
    border-radius: 50%;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
    animation: pop-in 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    z-index: 1;

    .v-icon {
      font-size: 36px !important;
    }
  }

  &__save-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #ff5252; // or your preferred accent color
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: bold;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &__original {
    position: relative;
    color: #999999;
    font-size: 1.2rem;
    margin-right: 8px;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 100%;
      height: 1px;
      background-color: #ff5252;
      transform: rotate(-10deg);
    }
  }

  &__footer {
    background-color: rgba(204, 204, 204, 0.12);
    text-align: center;
    padding: 12px;
    font-weight: 600;
    transition: all 0.3s ease;
    border-top: 1px solid #eee;
    color: darkslategrey;

    &:hover {
      color: black;
    }

    &--selected {
      background-color: #ffb20f;
      color: black;
    }

    &.default {
      &:hover {
        background-color: rgba(154, 152, 152, 0.12);
      }

      &.pricing-card__footer--selected {
        background: #ffb20f;
        color: black;
      }
    }

    &.pro {
      &:hover {
        background-color: #2980b9;
      }

      &.pricing-card__footer--selected {
        background-color: #ffb20f;
        color: white;
      }
    }

    &.business {
      &:hover {
        background-color: #2980b9;
      }

      &.pricing-card__footer--selected {
        background-color: #ffb20f;
        color: white;
      }
    }

    &.enterprise {
      &:hover {
        background-color: #2980b9;
      }

      &.pricing-card__footer--selected {
        background-color: #ffb20f;
        color: white;
      }
    }
  }
}

@keyframes pop-in {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

@media only screen and (max-width: $mobile) {
  .pricing-card {
    min-width: 100%;
  }
}
</style>
